import { __assign, __extends, __makeTemplateObject, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { OutsideClick } from '../utils/OutsideClick';
import { Input } from './Input';
import { Chevron } from '../Select/components/Chevron';
import { Wrapper } from '../Select/components/Wrapper';
import { SuggestionField, } from '../SuggestionField';
import { getLabelOf, findSuggestionByValue } from './util';
var defaultSearchMatch = function (searchQuery, term) {
    return term.toLowerCase().indexOf(searchQuery.toLowerCase()) === 0;
};
var calculateSearchQuery = function (value, props) {
    var children = props.children;
    var suggestion = findSuggestionByValue(children, value);
    return (suggestion && getLabelOf(suggestion)) || value;
};
var InputWithSuggestionsComponent = /** @class */ (function (_super) {
    __extends(InputWithSuggestionsComponent, _super);
    function InputWithSuggestionsComponent(props) {
        var _this = _super.call(this, props) || this;
        _this.inputElement = React.createRef();
        _this.suggestionField = React.createRef();
        _this.handleInputChange = function (e) {
            var value = e.target.value;
            var onChange = _this.props.onChange;
            _this.setState({
                searchQuery: value,
            }, function () { return onChange(e, value, value); });
        };
        _this.handleOutsideClick = function (event) {
            var onOutsideClick = _this.props.onOutsideClick;
            _this.setState({ areOptionsVisible: false });
            if (onOutsideClick) {
                onOutsideClick(event);
            }
        };
        _this.handleInputFocus = function (e) {
            var _a = _this.props, onFocus = _a.onFocus, shouldShowOptionsWhenEmpty = _a.shouldShowOptionsWhenEmpty, value = _a.value, isReadOnly = _a.isReadOnly, onSuggestionsShow = _a.onSuggestionsShow;
            if (isReadOnly)
                return;
            var searchQuery = calculateSearchQuery(value, _this.props);
            var areOptionsVisible = shouldShowOptionsWhenEmpty || !!value;
            if (areOptionsVisible && !!onSuggestionsShow) {
                onSuggestionsShow();
            }
            _this.setState({
                areOptionsVisible: areOptionsVisible,
                searchQuery: searchQuery,
            }, function () {
                if (_this.inputElement.current) {
                    _this.inputElement.current.selectionStart = 0;
                    _this.inputElement.current.selectionEnd = searchQuery.length;
                }
            });
            if (onFocus)
                onFocus(e);
        };
        _this.handleInputBlur = function (e) {
            var areOptionsVisible = _this.state.areOptionsVisible;
            var onBlur = _this.props.onBlur;
            if (areOptionsVisible)
                e.preventDefault();
            if (onBlur)
                onBlur(e);
        };
        _this.handleInputKeyDown = function (e) {
            var key = e.key;
            var areOptionsVisible = _this.state.areOptionsVisible;
            var _a = _this.props, onKeyDown = _a.onKeyDown, value = _a.value, shouldShowOptionsWhenEmpty = _a.shouldShowOptionsWhenEmpty, isReadOnly = _a.isReadOnly, onSuggestionsShow = _a.onSuggestionsShow;
            if (isReadOnly)
                return;
            var ignoreKey = e.ctrlKey ||
                e.metaKey ||
                e.altKey ||
                key === 'ArrowLeft' ||
                key === 'ArrowRight' ||
                key === 'Shift';
            if (ignoreKey)
                return;
            // passing event down to SuggestionField because it does
            // not have the focus but should react to keys nonetheless
            if (areOptionsVisible && _this.suggestionField.current) {
                _this.suggestionField.current.handleKeyDown(e);
            }
            switch (key) {
                case 'Backspace':
                    if (!value && !shouldShowOptionsWhenEmpty) {
                        _this.setState({
                            areOptionsVisible: false,
                        });
                    }
                    else if (value) {
                        _this.setState({
                            areOptionsVisible: true,
                        });
                    }
                    break;
                case 'Tab':
                    _this.setState({
                        areOptionsVisible: false,
                        searchQuery: value,
                    });
                    break;
                case 'Escape':
                    // in some browsers, Esc clears the field if type="search":
                    e.preventDefault();
                    _this.setState({
                        areOptionsVisible: false,
                    });
                    break;
                case 'ArrowUp':
                case 'ArrowDown':
                    e.preventDefault();
                    if (!areOptionsVisible) {
                        if (onSuggestionsShow) {
                            onSuggestionsShow();
                        }
                        _this.setState({ areOptionsVisible: true });
                    }
                    break;
                case 'Enter':
                    _this.setState({
                        areOptionsVisible: false,
                    });
                    break;
                default:
                    _this.setState({ areOptionsVisible: true });
            }
            if (onKeyDown)
                onKeyDown(e);
        };
        _this.handleOnSelectionChange = function (e, value, displayName) {
            var _a = _this.props, onChange = _a.onChange, onSelectionChange = _a.onSelectionChange;
            onChange(e, value, displayName);
            if (onSelectionChange)
                onSelectionChange(e, value, displayName);
            _this.setState({
                areOptionsVisible: false,
                searchQuery: displayName,
            });
        };
        _this.handleIconClick = function (event) {
            var onIconClick = _this.props.onIconClick;
            _this.setState({
                areOptionsVisible: false,
            });
            if (onIconClick)
                onIconClick(event);
        };
        _this.filterSuggestions = function () {
            var _a = _this.props, children = _a.children, searchMatch = _a.searchMatch;
            var searchQuery = _this.state.searchQuery;
            return React.Children.toArray(children).filter(function (suggestion) {
                var suggestionValue = getLabelOf(suggestion);
                return searchMatch
                    ? searchMatch(searchQuery, suggestionValue)
                    : defaultSearchMatch(searchQuery, suggestionValue);
            });
        };
        _this.state = {
            areOptionsVisible: false,
            searchQuery: calculateSearchQuery(props.value, props),
        };
        return _this;
    }
    // props and state are any because of type inference problems in styled()
    InputWithSuggestionsComponent.getDerivedStateFromProps = function (props, state) {
        if (state.areOptionsVisible)
            return null;
        var value = props.value;
        var searchQuery = calculateSearchQuery(value, props);
        if (searchQuery !== state.searchQuery) {
            return { searchQuery: searchQuery };
        }
        return null;
    };
    InputWithSuggestionsComponent.prototype.render = function () {
        var _this = this;
        var _a = this.props, id = _a.id, onChange = _a.onChange, value = _a.value, className = _a.className, shouldShowOptionsWhenEmpty = _a.shouldShowOptionsWhenEmpty, maxVisibleOptions = _a.maxVisibleOptions, _b = _a.noOptionFound, noOptionFound = _b === void 0 ? 'No option found' : _b, _c = _a.shouldShowChevron, shouldShowChevron = _c === void 0 ? false : _c, children = _a.children, onSelectionChange = _a.onSelectionChange, onSuggestionsShow = _a.onSuggestionsShow, onOutsideClick = _a.onOutsideClick, onIconClick = _a.onIconClick, _d = _a.htmlAutoComplete, htmlAutoComplete = _d === void 0 ? 'off' : _d, placeholder = _a.placeholder, maxResults = _a.maxResults, suggestionFieldZIndex = _a.suggestionFieldZIndex, _e = _a.shouldFocusFirstEntry, shouldFocusFirstEntry = _e === void 0 ? false : _e, isDisabled = _a.isDisabled, isReadOnly = _a.isReadOnly, inputProps = __rest(_a, ["id", "onChange", "value", "className", "shouldShowOptionsWhenEmpty", "maxVisibleOptions", "noOptionFound", "shouldShowChevron", "children", "onSelectionChange", "onSuggestionsShow", "onOutsideClick", "onIconClick", "htmlAutoComplete", "placeholder", "maxResults", "suggestionFieldZIndex", "shouldFocusFirstEntry", "isDisabled", "isReadOnly"]);
        var _f = this.state, areOptionsVisible = _f.areOptionsVisible, searchQuery = _f.searchQuery;
        var suggestions = this.filterSuggestions();
        var shouldShowOptions = (!!value || shouldShowOptionsWhenEmpty) &&
            (suggestions.length > 0 || noOptionFound) &&
            areOptionsVisible;
        return (React.createElement(OutsideClick, { onOutsideClick: this.handleOutsideClick }, function (_a) {
            var outsideClickRef = _a.outsideClickRef;
            return (React.createElement(Wrapper, { ref: outsideClickRef },
                React.createElement(Input, __assign({}, inputProps, { placeholder: !value ? placeholder : undefined, id: id, ref: _this.inputElement, className: className, 
                    // since some browsers ignore "off", we allow developers to change it:
                    autoComplete: htmlAutoComplete, value: searchQuery, onFocus: _this.handleInputFocus, onKeyDown: _this.handleInputKeyDown, onBlur: _this.handleInputBlur, onChange: _this.handleInputChange, onIconClick: _this.handleIconClick, isDisabled: isDisabled, isReadOnly: isReadOnly })),
                shouldShowOptions && (React.createElement(SuggestionField, { maxVisibleOptions: maxVisibleOptions, value: value, onChange: _this.handleOnSelectionChange, noOptionFound: noOptionFound, maxResults: maxResults, ref: _this.suggestionField, autoFocus: true, zIndex: suggestionFieldZIndex, 
                    // mutes SuggestionField's internal key handling:
                    onKeyDown: undefined, shouldFocusFirstEntry: shouldFocusFirstEntry }, suggestions)),
                shouldShowChevron && (React.createElement(Chevron, { isDisabled: isDisabled, isReadOnly: isReadOnly, "data-testid": "chevron" }))));
        }));
    };
    return InputWithSuggestionsComponent;
}(React.Component));
export { InputWithSuggestionsComponent };
export var InputWithSuggestions = styled(InputWithSuggestionsComponent)(templateObject_1 || (templateObject_1 = __makeTemplateObject([""], [""])));
export var InputWithSuggestionsPropTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    shouldShowOptionsWhenEmpty: PropTypes.bool,
    shouldShowChevron: PropTypes.bool,
    noOptionFound: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onSelectionChange: PropTypes.func,
    onSuggestionsShow: PropTypes.func,
    onOutsideClick: PropTypes.func,
    maxResults: PropTypes.number,
    maxVisibleOptions: PropTypes.number,
    htmlAutoComplete: PropTypes.string,
    searchMatch: PropTypes.func,
    suggestionFieldZIndex: PropTypes.number,
    shouldFocusFirstEntry: PropTypes.bool,
    children: PropTypes.node,
    isReadOnly: PropTypes.bool,
};
InputWithSuggestions.propTypes = InputWithSuggestionsPropTypes;
var templateObject_1;
//# sourceMappingURL=InputWithSuggestions.js.map